import { encodeBearerToken, filterInstagramCookies } from '../cookies'
import { ParseError } from '../errors'
import { type AccountData, emptyAccount } from '../models'

export function splitWebJsonRecords(data: string): string[] {
  return data
    .trim()
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
}

export function canParseWebJson(data: string): boolean {
  for (const raw of data.trim().split('\n')) {
    const line = raw.trim()
    if (!line) continue
    if (!line.includes('[') || !line.includes(']')) continue
    const before = line.split('[')[0]
    if ((before.match(/:/g) ?? []).length !== 1) continue
    const open = line.indexOf('[')
    const close = line.lastIndexOf(']')
    const jsonPart = line.slice(open + 1, close)
    try {
      const parsed = JSON.parse(`[${jsonPart}]`) as unknown
      if (
        Array.isArray(parsed) &&
        parsed.length > 0 &&
        typeof parsed[0] === 'object' &&
        parsed[0] !== null &&
        'domain' in parsed[0]
      ) {
        return true
      }
    } catch {
      continue
    }
  }
  return false
}

export function parseWebJson(record: string): AccountData {
  const r = record.trim()
  if (!r.includes('[') || !r.includes(']')) {
    throw new ParseError('Missing bracket-enclosed cookies')
  }
  const open = r.indexOf('[')
  const close = r.lastIndexOf(']')
  const loginPart = r.slice(0, open)
  const jsonPart = r.slice(open + 1, close)

  if ((loginPart.match(/:/g) ?? []).length !== 1) {
    throw new ParseError(`Expected login:[json], got ${loginPart}`)
  }

  const login = loginPart.replace(/:$/, '').trim()
  if (!login) throw new ParseError('Empty login')

  let cookieObjects: Array<Record<string, string>>
  try {
    cookieObjects = JSON.parse(`[${jsonPart}]`) as Array<Record<string, string>>
  } catch (e) {
    throw new ParseError(`Invalid JSON cookies: ${(e as Error).message}`)
  }

  const cookies = filterInstagramCookies(cookieObjects)
  if (!('sessionid' in cookies) || !('ds_user_id' in cookies)) {
    throw new ParseError('Missing required cookies: sessionid, ds_user_id')
  }

  const bearer = encodeBearerToken(cookies.sessionid, cookies.ds_user_id)
  cookies.Authorization = `Bearer ${bearer}`

  return { ...emptyAccount(), login, cookies }
}
